import { useEffect, useMemo, useState } from "react";
import styles from "./AdminShared.module.css";
import local from "./AdminTracking.module.css";
import { useAdminUI } from "./AdminUIContext";
import { setTrackAdjustment } from "../../../storage/trackDB";

export const formatSignedCount = (n, singular, plural) => {
  const value = Number(n) || 0;
  const noun = Math.abs(value) === 1 ? singular : plural;
  const sign = value > 0 ? "+" : "";
  return `${sign}${value} ${noun}`;
};

export const formatAdjustmentSummary = (adj = {}) => {
  const parts = [];
  if (adj.landingsDelta) {
    parts.push(formatSignedCount(adj.landingsDelta, "landing", "landings"));
  }
  if (adj.signupsDelta) {
    parts.push(formatSignedCount(adj.signupsDelta, "signup", "signups"));
  }
  return parts.join(", ");
};

const parseInputCount = (value) => {
  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
};

const AdminTrackCountAdjust = ({ summary, days, user, onSummary }) => {
  const { toast, confirm } = useAdminUI();
  const totals = summary?.totals || { landings: 0, signups: 0 };
  const raw = summary?.rawTotals || totals;
  const adj = summary?.adjustment || {};
  const active = Boolean(adj.active);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [landings, setLandings] = useState(String(totals.landings ?? 0));
  const [signups, setSignups] = useState(String(totals.signups ?? 0));

  useEffect(() => {
    setLandings(String(totals.landings ?? 0));
    setSignups(String(totals.signups ?? 0));
  }, [totals.landings, totals.signups]);

  const nextLandings = parseInputCount(landings);
  const nextSignups = parseInputCount(signups);
  const dirty = useMemo(() => {
    if (nextLandings == null || nextSignups == null) return false;
    return nextLandings !== totals.landings || nextSignups !== totals.signups;
  }, [nextLandings, nextSignups, totals.landings, totals.signups]);

  const save = async () => {
    if (nextLandings == null || nextSignups == null || !dirty) return;
    setBusy(true);
    try {
      const data = await setTrackAdjustment(user, {
        landings: nextLandings,
        signups: nextSignups,
        days,
      });
      onSummary?.(data);
      window.dispatchEvent(new CustomEvent("ms1-admin-refresh-tracking"));
      toast.success("Displayed counts updated.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update the counts.",
      );
    } finally {
      setBusy(false);
    }
  };

  const clearCorrection = async () => {
    const ok = await confirm({
      title: "Clear the count correction?",
      message:
        "Headline landings and signups will show the recorded events again.",
      confirmLabel: "Clear correction",
      cancelLabel: "Keep correction",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const data = await setTrackAdjustment(user, { clear: true, days });
      onSummary?.(data);
      window.dispatchEvent(new CustomEvent("ms1-admin-refresh-tracking"));
      toast.success("Showing recorded counts.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not clear the correction.",
      );
    } finally {
      setBusy(false);
    }
  };

  const summaryLabel = formatAdjustmentSummary(adj);

  return (
    <div className={local["count-adjust"]}>
      <button
        type="button"
        className={`${local["count-adjust-toggle"]} ${
          active ? local["count-adjust-toggle-active"] : ""
        }`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {active
          ? `Counts corrected (${summaryLabel}). Edit`
          : "Correct counts"}
      </button>
      {open ? (
        <div className={local["count-adjust-panel"]}>
          <p className={local["count-adjust-copy"]}>
            Set the headline totals for this range — for a test visit or a
            signup before Exclude. The same offset applies to every date range
            and to Export JSON. Recorded events and breakdown rows stay as they
            are. New visits still count.
          </p>
          <p className={styles["field-hint"]}>
            Recorded in this range: {raw.landings ?? 0} landings ·{" "}
            {raw.signups ?? 0} signups
            {active ? ` · offset ${summaryLabel}` : ""}
          </p>
          <div className={local["count-adjust-fields"]}>
            <label>
              Landings
              <input
                className={styles["text-input"]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={landings}
                onChange={(e) => setLandings(e.target.value)}
                disabled={busy}
                aria-label="Displayed landings"
              />
            </label>
            <label>
              Signups
              <input
                className={styles["text-input"]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={signups}
                onChange={(e) => setSignups(e.target.value)}
                disabled={busy}
                aria-label="Displayed signups"
              />
            </label>
          </div>
          <div className={local["count-adjust-actions"]}>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={save}
              disabled={busy || !dirty || nextLandings == null || nextSignups == null}
            >
              {busy ? "Saving…" : "Save"}
            </button>
            {active ? (
              <button
                type="button"
                className={styles["btn-ghost"]}
                onClick={clearCorrection}
                disabled={busy}
              >
                Use recorded counts
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminTrackCountAdjust;
